import initiateLoadDynamicScripts from '../../utils/initiateLoadDynamicScripts';
// https://developers.google.com/maps/documentation/javascript/geocoding
export const googleMapsBaseUrl = 'https://maps.googleapis.com/maps/api/js';
export const defaultOptions = {
    // https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
    placeFields: {
        administrative_area_level_1: 'long_name',
        country: 'short_name',
        locality: 'long_name',
        postal_code: 'long_name',
        postal_town: 'long_name',
        route: 'long_name',
        street_number: 'long_name',
        sublocality: 'long_name',
    },
    searchOptions: {},
};
export default class GoogleMapsModule {
    constructor(accessToken, options) {
        /**
         * Extracts suggestions from Search response.
         */
        this.formatSearchResult = GoogleMapsModule.formatSearchResult;
        /**
         * Gets a human-readable label for the suggestion location.
         */
        this.getSuggestionLabel = GoogleMapsModule.getSuggestionLabel;
        this.accessToken = accessToken;
        this.options = Object.assign(Object.assign({}, defaultOptions), options);
        initiateLoadDynamicScripts([
            {
                callback: this.privateInitAutocomplete.bind(this),
                id: 'googleMapsApi',
                url: GoogleMapsModule.getGoogleMapsApiUrl(accessToken),
            },
        ]);
    }
    static formatSearchResult(searchResult) {
        return searchResult;
    }
    static getGoogleMapsApiUrl(key) {
        return `${googleMapsBaseUrl}?libraries=places&key=${key}`;
    }
    static getSuggestionLabel(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    suggestion) {
        return suggestion.description;
    }
    privateFormatAddressComponents(addressComponents) {
        return ((Object
            .entries(this.options.placeFields))
            .reduce((acc, [type, name,]) => {
            const addressComponent = ((addressComponents === null || addressComponents === void 0 ? void 0 : addressComponents.find(({ types, }) => (types.includes(type)))));
            acc[type] = addressComponent && name ? addressComponent[name] : undefined;
            return acc;
        }, {}));
    }
    privateGeocodeRequest(placeId) {
        return (new Promise((resolve, reject) => {
            var _a;
            (_a = this.geocoder) === null || _a === void 0 ? void 0 : _a.geocode({
                placeId,
            }, (results, status) => {
                if (status !== this.geocoderStatusOK) {
                    reject(status);
                    return;
                }
                resolve(results);
            });
        }));
    }
    privateInitAutocomplete() {
        if (!window.google || !window.google.maps.places) {
            return;
        }
        this.autocompleteService = new window.google.maps.places.AutocompleteService();
        this.geocoder = new window.google.maps.Geocoder();
        this.autocompleteServiceStatusOK = window.google.maps.places.PlacesServiceStatus.OK;
        this.geocoderStatusOK = window.google.maps.GeocoderStatus.OK;
    }
    privateSearchRequest(searchText) {
        return (new Promise((resolve, reject) => {
            var _a;
            (_a = this.autocompleteService) === null || _a === void 0 ? void 0 : _a.getPlacePredictions(Object.assign(Object.assign({}, this.options.searchOptions), { input: searchText }), (suggestions, status) => {
                if (status !== this.autocompleteServiceStatusOK) {
                    reject(status);
                    return;
                }
                resolve(suggestions);
            });
        }));
    }
    async geocode(placeId, applyAddressComponentsFormatting = true) {
        const geocodeResponse = (await this.privateGeocodeRequest(placeId)
            .catch((status) => {
            throw new Error(`GoogleMapsModule(geocode): Could not process geocoding request. STATUS: "${status}"\nDocs: https://developers.google.com/maps/documentation/javascript/reference/geocoder#GeocoderStatus`);
        }));
        return (applyAddressComponentsFormatting
            ? this.privateFormatAddressComponents(geocodeResponse === null || geocodeResponse === void 0 ? void 0 : geocodeResponse[0].address_components)
            : geocodeResponse);
    }
    /**
     * Requests suggestions from map service and returns response data.
     */
    async search(searchText) {
        const cleanedText = searchText.trim();
        if (!cleanedText) {
            throw new Error(`GoogleMapsModule(search): Must provide a non-empty 'searchText'(string) value, you provided: "${cleanedText}"`);
        }
        return this.privateSearchRequest(searchText);
    }
}
